/**
 * Header search controller.
 *
 * Binds to the [data-search] root in the nav, manages the input -> debounce ->
 * adapter -> render flow. Talks to window.SearchaniseAdapter only; never touches
 * window.SnsApi directly. Keeps the data layer (adapter) and the UI layer
 * (this file) cleanly separated.
 *
 * Behaviors wired:
 *   - input event handler with 250ms debounce + AbortController
 *   - render functions for each dropdown section (suggestions, facets,
 *     products, view-all CTA)
 *   - typeahead match-highlight via <mark>
 *   - close handlers: outside click, Escape key, clear / back buttons
 *   - keyboard navigation (Arrow / Home / End / Enter, aria-activedescendant)
 *   - open-on-focus and empty/loading/error states (initial popular searches,
 *     zero-results message with the user's query, network-error fallback)
 *   - delegated click activation for [role="option"] rows
 */
(function () {
  'use strict';

  const ROOT_SELECTOR = '[data-search]';
  const DEBOUNCE_MS = 250;
  const MIN_QUERY_LENGTH = 2;

  function init() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initOne);
  }

  function initOne(root) {
    const elements = collectElements(root);
    if (!elements.input || !elements.dropdown) return;

    const state = {
      query: '',
      result: null,
      activeAbort: null,
      activeRowId: null
    };

    bindInput(root, elements, state);
    bindCloseHandlers(root, elements, state);
    bindFormButtons(root, elements, state);
    bindKeyboardNav(root, elements, state);
    bindRowClicks(root, elements);
  }

  /* Single source of truth for open/close transitions. CSS reacts to
     [data-open] on the root; aria-expanded mirrors it on the input so
     assistive tech sees the same state. */
  function open(root, elements) {
    root.dataset.open = 'true';
    elements.input.setAttribute('aria-expanded', 'true');
  }

  function close(root, elements, state) {
    root.dataset.open = 'false';
    elements.input.setAttribute('aria-expanded', 'false');
    resetActiveRow(root, elements, state);
  }

  /* Clears any [data-active="true"] highlight and the input's
     aria-activedescendant. Called on close and before every render so
     stale row references don't outlive the rows themselves. */
  function resetActiveRow(root, elements, state) {
    if (state.activeRowId) {
      const prev = root.querySelector('#' + CSS.escape(state.activeRowId));
      if (prev) delete prev.dataset.active;
    }
    state.activeRowId = null;
    elements.input.removeAttribute('aria-activedescendant');
  }

  function bindCloseHandlers(root, elements, state) {
    /* Outside click: pointerdown (not click) so the close fires before any
       focus shift inside the dropdown completes, matching native combobox
       feel. Skip when target is inside the search root.

       Edge case: clicks on .c-search::before (the desktop backdrop pseudo)
       report the host element itself (event.target === root). Pseudo-
       elements aren't in the DOM tree, so contains() can't tell them apart
       from a click on real .c-search content. We treat target===root as
       outside since real descendants always have target !== root. */
    document.addEventListener('pointerdown', function (event) {
      if (root.dataset.open !== 'true') return;
      if (event.target !== root && root.contains(event.target)) return;
      close(root, elements, state);
    });

    /* Escape closes from anywhere and returns focus to the input so a
       second tab doesn't dump the user into unrelated chrome. */
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      if (root.dataset.open !== 'true') return;
      close(root, elements, state);
      elements.input.focus();
    });
  }

  function bindFormButtons(root, elements, state) {
    /* Back button (mobile-open only, hidden via CSS on desktop): close the
       drawer without clearing the input. The query stays so the user can
       resume where they left off. Blur the input explicitly — without
       this, the focus handler (open-on-focus) re-opens the drawer the
       moment close() finishes, so the user sees nothing happen. */
    if (elements.back) {
      elements.back.addEventListener('click', function () {
        close(root, elements, state);
        elements.input.blur();
      });
    }

    /* Clear button: empty the input and fire an input event. The input
       handler sees an empty query and falls through to showEmptyState
       ('initial'), so the drawer stays open with popular searches —
       only Escape / outside-click / back actually close it. */
    if (elements.clear) {
      elements.clear.addEventListener('click', function () {
        elements.input.value = '';
        elements.input.dispatchEvent(new Event('input', { bubbles: true }));
        elements.input.focus();
      });
    }
  }

  /* Delegated click activation for any [role="option"] row. Two row
     contracts are supported:
       - data-search-url: navigate (or defer to a child <a>)
       - data-search-value: fill the input and re-fire search via the
         existing input event handler (used by suggestions + popular)
     Rows can specify either or both; url wins when both exist. */
  function bindRowClicks(root, elements) {
    root.addEventListener('click', function (event) {
      const row = event.target.closest('[role="option"]');
      if (!row) return;

      const url = row.dataset.searchUrl;
      if (url) {
        if (event.target.closest('a')) return;  /* native anchor handles it */
        event.preventDefault();
        window.location.href = url;
        return;
      }

      const value = row.dataset.searchValue;
      if (value) {
        event.preventDefault();
        elements.input.value = value;
        elements.input.dispatchEvent(new Event('input', { bubbles: true }));
        elements.input.focus();
      }
    });
  }

  /* ===========================================================
     Keyboard navigation (ARIA combobox pattern)
     - Arrow Up/Down: cycle through visible options across sections
     - Home/End: first / last option
     - Enter: activate the active option (navigate to its url),
       otherwise fall through to the form's /search submit
     - aria-activedescendant on the input tracks the active row's id
       so screen readers announce it without DOM focus shifts
     =========================================================== */

  function bindKeyboardNav(root, elements, state) {
    elements.input.addEventListener('keydown', function (event) {
      const rows = collectVisibleRows(root);
      if (!rows.length) {
        if (event.key === 'Enter' && state.activeRowId) state.activeRowId = null;
        return;
      }

      const currentIndex = state.activeRowId
        ? rows.findIndex(function (r) { return r.id === state.activeRowId; })
        : -1;

      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault();
          const next = currentIndex < rows.length - 1 ? currentIndex + 1 : 0;
          setActiveRow(elements, rows, next, state);
          break;
        }
        case 'ArrowUp': {
          event.preventDefault();
          const prev = currentIndex > 0 ? currentIndex - 1 : rows.length - 1;
          setActiveRow(elements, rows, prev, state);
          break;
        }
        case 'Home': {
          if (root.dataset.open !== 'true') return;
          event.preventDefault();
          setActiveRow(elements, rows, 0, state);
          break;
        }
        case 'End': {
          if (root.dataset.open !== 'true') return;
          event.preventDefault();
          setActiveRow(elements, rows, rows.length - 1, state);
          break;
        }
        case 'Enter': {
          if (currentIndex === -1) return;  /* let form submit naturally */
          const row = rows[currentIndex];
          const url = row.dataset.searchUrl || (row.querySelector('a') && row.querySelector('a').href);
          if (url) {
            event.preventDefault();
            window.location.href = url;
            return;
          }
          const value = row.dataset.searchValue;
          if (value) {
            event.preventDefault();
            elements.input.value = value;
            elements.input.dispatchEvent(new Event('input', { bubbles: true }));
          }
          break;
        }
      }
    });
  }

  function collectVisibleRows(root) {
    /* Flat ordered list of every [role="option"] inside a section whose
       data-has-content === "true". Section / group visibility is driven
       by the same attribute so this matches what the user sees. */
    return Array.prototype.slice.call(
      root.querySelectorAll(
        '[data-search-section][data-has-content="true"] [role="option"]'
      )
    );
  }

  function setActiveRow(elements, rows, index, state) {
    rows.forEach(function (row, i) {
      if (i === index) {
        row.dataset.active = 'true';
        row.scrollIntoView({ block: 'nearest' });
      } else {
        delete row.dataset.active;
      }
    });
    state.activeRowId = rows[index].id;
    elements.input.setAttribute('aria-activedescendant', rows[index].id);
  }

  function bindInput(root, elements, state) {
    /* Each keystroke past MIN_QUERY_LENGTH schedules a debounced search.
       AbortController cancels the previous in-flight request so slow
       responses to old queries can't stomp on results for newer ones.

       Resolve path: clear loading flag, then either render results or
       show the zero-results empty state (with the user's query echoed).
       Reject path: clear loading and show the error variant of empty
       state, unless it was an AbortError (newer query already in flight
       will clear loading on its own resolve). */
    const debouncedSearch = debounce(async function (query) {
      if (state.activeAbort) state.activeAbort.abort();
      state.activeAbort = new AbortController();

      try {
        const result = await window.SearchaniseAdapter.search(query, {
          signal: state.activeAbort.signal
        });
        state.result = result;
        resetActiveRow(root, elements, state);
        root.dataset.loading = 'false';

        if (!result || !result.total) {
          showEmptyState(elements, 'zero-results', query);
          return;
        }
        hideEmptyState(elements);
        renderAll(elements, result, query);
      } catch (err) {
        if (err.name === 'AbortError') return;
        /* Distinguish expected "SDK not installed" from real failures so
           noisy ops monitoring doesn't fire on the former. Both paths
           render the same user-visible error variant of the empty state. */
        if (err.name === 'SearchUnavailableError') {
          console.warn('[search] unavailable:', err.message);
        } else {
          console.error('[search] error', err);
        }
        root.dataset.loading = 'false';
        showEmptyState(elements, 'error', query);
      }
    }, DEBOUNCE_MS);

    /* Input event: any change to the input value, whether typed or
       dispatched programmatically (clear button, popular-search click). */
    elements.input.addEventListener('input', function (event) {
      const query = event.target.value.trim();
      state.query = query;

      open(root, elements);

      if (query.length < MIN_QUERY_LENGTH) {
        /* Below-threshold input: no fetch, show popular-search list as
           the initial discovery surface. Clear loading and any stale
           active-row reference. */
        root.dataset.loading = 'false';
        resetActiveRow(root, elements, state);
        showEmptyState(elements, 'initial', '');
        return;
      }

      root.dataset.loading = 'true';
      debouncedSearch(query);
    });

    /* Open-on-focus: clicking or tabbing into the input opens the drawer
       even before the user types. If there's no query (or one below the
       threshold), show the initial popular-search surface. */
    elements.input.addEventListener('focus', function () {
      open(root, elements);
      const query = elements.input.value.trim();
      if (query.length < MIN_QUERY_LENGTH) {
        root.dataset.loading = 'false';
        showEmptyState(elements, 'initial', '');
      }
    });
  }

  function debounce(fn, wait) {
    let timer;
    return function () {
      const args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(null, args); }, wait);
    };
  }

  function collectElements(root) {
    return {
      root: root,
      input: root.querySelector('[data-search-input]'),
      back: root.querySelector('[data-search-back]'),
      clear: root.querySelector('[data-search-clear]'),
      dropdown: root.querySelector('[data-search-dropdown]'),
      sectionSuggestions: root.querySelector('[data-search-section="suggestions"]'),
      sectionFacets: root.querySelector('[data-search-section="facets"]'),
      sectionProducts: root.querySelector('[data-search-section="products"]'),
      listSuggestions: root.querySelector('[data-search-list="suggestions"]'),
      facetGroups: {
        activity: root.querySelector('[data-search-facet-group="activity"]'),
        surface: root.querySelector('[data-search-facet-group="surface"]'),
        stain: root.querySelector('[data-search-facet-group="stain"]')
      },
      facetLists: {
        activity: root.querySelector('[data-search-list="facet"][data-search-facet="activity"]'),
        surface: root.querySelector('[data-search-list="facet"][data-search-facet="surface"]'),
        stain: root.querySelector('[data-search-list="facet"][data-search-facet="stain"]')
      },
      listProducts: root.querySelector('[data-search-list="products"]'),
      sectionEmpty: root.querySelector('[data-search-section="empty"]'),
      emptyQuery: root.querySelector('[data-search-empty-query]'),
      cta: root.querySelector('[data-search-cta]'),
      ctaText: root.querySelector('[data-search-cta-text]')
    };
  }

  /* ===========================================================
     Empty / initial / error state
     One section in the dropdown handles all three unhappy paths
     by toggling data-search-empty-state. JS hides every other
     section (and the CTA) when this one is active.
     =========================================================== */

  function showEmptyState(elements, variant, query) {
    if (!elements.sectionEmpty) return;
    elements.sectionEmpty.dataset.hasContent = 'true';
    elements.sectionEmpty.dataset.searchEmptyState = variant;
    if (elements.emptyQuery) {
      elements.emptyQuery.textContent = query || '';
    }
    if (elements.sectionSuggestions) elements.sectionSuggestions.dataset.hasContent = 'false';
    if (elements.sectionFacets) elements.sectionFacets.dataset.hasContent = 'false';
    if (elements.sectionProducts) elements.sectionProducts.dataset.hasContent = 'false';
    if (elements.cta) elements.cta.dataset.hasContent = 'false';
  }

  function hideEmptyState(elements) {
    if (!elements.sectionEmpty) return;
    elements.sectionEmpty.dataset.hasContent = 'false';
  }

  /* ===========================================================
     Render functions
     Each renderer clears its list via replaceChildren() then
     builds new DOM via createElement (textContent only, never
     innerHTML, per project XSS guard).
     =========================================================== */

  function renderAll(elements, result, query) {
    renderSuggestions(elements, result.suggestions, query);
    renderFacets(elements, result.facets);
    renderProducts(elements, result.products);
    renderCta(elements, result.total, query);
  }

  function renderSuggestions(elements, suggestions, query) {
    const ul = elements.listSuggestions;
    const section = elements.sectionSuggestions;
    ul.replaceChildren();

    if (!suggestions || !suggestions.length) {
      section.dataset.hasContent = 'false';
      return;
    }

    const iconTemplate = section.querySelector('[data-search-suggestion-icon]');

    suggestions.forEach(function (text, i) {
      const li = document.createElement('li');
      li.id = 'c-search-row-suggestion-' + i;
      li.className = 'u-animation-slide-in-left';
      li.style.setProperty('--index', i + 1);
      li.setAttribute('role', 'option');
      li.dataset.searchRow = 'suggestion';
      li.dataset.searchValue = text;
      if (iconTemplate) {
        li.appendChild(iconTemplate.content.cloneNode(true));
      }
      const textWrap = document.createElement('span');
      textWrap.appendChild(highlightCompletion(query, text));
      li.appendChild(textWrap);
      ul.appendChild(li);
    });

    section.dataset.hasContent = 'true';
  }

  /* Wraps the completion portion of a suggestion in <mark>.
     Case-insensitive prefix match: if `fullText` starts with `query`,
     split at query.length and wrap the suffix. Otherwise render plain.
     Returns a DocumentFragment built via createElement (no innerHTML
     ever, even for trusted strings, so the rule stays simple). */
  function highlightCompletion(query, fullText) {
    const fragment = document.createDocumentFragment();
    if (!query) {
      fragment.appendChild(document.createTextNode(fullText));
      return fragment;
    }

    const lowerText = fullText.toLowerCase();
    const lowerQuery = query.toLowerCase();

    if (!lowerText.startsWith(lowerQuery)) {
      fragment.appendChild(document.createTextNode(fullText));
      return fragment;
    }

    const prefix = fullText.slice(0, query.length);
    const suffix = fullText.slice(query.length);

    fragment.appendChild(document.createTextNode(prefix));
    if (suffix.length) {
      const mark = document.createElement('mark');
      mark.textContent = suffix;
      fragment.appendChild(mark);
    }
    return fragment;
  }

  function renderFacets(elements, facets) {
    const section = elements.sectionFacets;
    let anyVisible = false;

    ['activity', 'surface', 'stain'].forEach(function (kind) {
      const matches = (facets && facets[kind]) || [];
      const group = elements.facetGroups[kind];
      const ul = elements.facetLists[kind];
      if (!group || !ul) return;
      ul.replaceChildren();

      if (!matches.length) {
        group.dataset.hasContent = 'false';
        return;
      }

      matches.forEach(function (match, i) {
        const li = document.createElement('li');
        li.id = 'c-search-row-facet-' + kind + '-' + i;
        li.className = 'u-animation-slide-in-left';
        li.style.setProperty('--index', i + 1);
        li.setAttribute('role', 'option');
        li.dataset.searchRow = 'facet';
        li.dataset.searchFacet = kind;
        li.dataset.searchUrl = match.url || '';

        const a = document.createElement('a');
        a.href = match.url || '#';
        a.textContent = match.value + ' (' + match.count + ')';
        li.appendChild(a);
        ul.appendChild(li);
      });

      group.dataset.hasContent = 'true';
      anyVisible = true;
    });

    section.dataset.hasContent = anyVisible ? 'true' : 'false';
  }

  function renderProducts(elements, products) {
    const ul = elements.listProducts;
    const section = elements.sectionProducts;
    ul.replaceChildren();

    if (!products || !products.length) {
      section.dataset.hasContent = 'false';
      return;
    }

    products.forEach(function (product, i) {
      const li = document.createElement('li');
      li.id = 'c-search-row-product-' + i;
      li.className = 'u-animation-slide-in-left';
      li.style.setProperty('--index', i + 1);
      li.setAttribute('role', 'option');
      li.dataset.searchRow = 'product';
      li.dataset.searchUrl = product.url;

      const a = document.createElement('a');
      a.href = product.url;
      a.className = 'c-search__product-link';

      if (product.imageUrl) {
        const img = document.createElement('img');
        img.src = product.imageUrl;
        img.alt = '';
        img.className = 'c-search__product-image';
        a.appendChild(img);
      }

      const meta = document.createElement('div');
      meta.className = 'c-search__product-meta';

      const title = document.createElement('span');
      title.className = 'c-search__product-title';
      title.textContent = product.title;
      meta.appendChild(title);

      if (product.description) {
        const desc = document.createElement('span');
        desc.className = 'c-search__product-description';
        desc.textContent = product.description;
        meta.appendChild(desc);
      }

      if (product.price) {
        const price = document.createElement('span');
        price.className = 'c-search__product-price';
        price.textContent = product.price;
        meta.appendChild(price);
      }

      a.appendChild(meta);
      li.appendChild(a);
      ul.appendChild(li);
    });

    section.dataset.hasContent = 'true';
  }

  function renderCta(elements, total, query) {
    if (!total || total === 0) {
      elements.cta.dataset.hasContent = 'false';
      return;
    }
    elements.cta.dataset.hasContent = 'true';
    elements.cta.href = '/search?q=' + encodeURIComponent(query);
    // i18n note: this string is hardcoded English for now. To use the locale
    // system (search.view_all_html with one/other pluralization), the CTA
    // template should expose the translated strings as data-* attributes
    // for the JS to read. Follow-up task.
    elements.ctaText.textContent = total === 1
      ? 'View the ' + total + ' matching item'
      : 'View all ' + total + ' items';
  }

  document.addEventListener('DOMContentLoaded', init);
})();
